import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Checkbox } from "../../../../ui/checkbox";
import { getNodeChildren } from "../../utils/utils";
import { DeleteModalState } from "../../hooks/hooks";

interface DeleteModalsProps {
  deleteModalState: DeleteModalState;
  isResetModalVisible: boolean;
  isDeleteInterjectionGroupModalVisible: boolean;
  interjectionGroupToDelete: any | null;
  edges: any[];
  setDeleteModalState: (state: DeleteModalState | ((prev: DeleteModalState) => DeleteModalState)) => void;
  onDeleteModalCancel: () => void;
  onDeleteNodeConfirm: () => void;
  onDeleteBranchConfirm: () => void;
  onResetConfirm: () => void;
  onResetCancel: () => void;
  onDeleteInterjectionGroupConfirm: () => void;
  onDeleteInterjectionGroupCancel: () => void;
}

const DeleteModals: React.FC<DeleteModalsProps> = ({
  deleteModalState,
  isResetModalVisible,
  isDeleteInterjectionGroupModalVisible,
  interjectionGroupToDelete,
  edges,
  setDeleteModalState,
  onDeleteModalCancel,
  onDeleteNodeConfirm,
  onDeleteBranchConfirm,
  onResetConfirm,
  onResetCancel,
  onDeleteInterjectionGroupConfirm,
  onDeleteInterjectionGroupCancel,
}) => {
  return (
    <>
      {/* Delete Node Confirmation Modal */}
      <Dialog open={deleteModalState.isDeleteNodeModalVisible} onOpenChange={(open) => { if (!open) onDeleteModalCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-gc-med text-foreground">
              Confirm Delete Node
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-gc-reg text-foreground">
              Are you sure you want to delete this node? This action cannot be undone.
            </p>
            {deleteModalState.nodeToDeleteFromMenu && getNodeChildren(deleteModalState.nodeToDeleteFromMenu, edges).length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="delete-children"
                    checked={deleteModalState.deleteChildren} 
                    onCheckedChange={(checked) => setDeleteModalState((prev: DeleteModalState) => ({ ...prev, deleteChildren: !!checked }))}
                  />
                  <label 
                    htmlFor="delete-children" 
                    className="text-sm font-gc-reg text-foreground cursor-pointer"
                  >
                    Delete children (deletes this node and all its descendants)
                  </label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onDeleteModalCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDeleteNodeConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Branch Confirmation Modal */}
      <Dialog open={deleteModalState.isDeleteBranchModalVisible} onOpenChange={(open) => { if (!open) onDeleteModalCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-gc-med text-foreground">
              Confirm Delete Branch
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-gc-reg text-foreground">
              Are you sure you want to delete this node and all its descendants? This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onDeleteModalCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDeleteBranchConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Modal */}
      <Dialog open={isResetModalVisible} onOpenChange={(open) => { if (!open) onResetCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-gc-med text-foreground">
              Confirm Reset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-gc-reg text-foreground">
              Are you sure you want to reset the conversation? This will delete all nodes and cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onResetCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onResetConfirm}>
              Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Interjection Group Confirmation Modal */}
      <Dialog open={isDeleteInterjectionGroupModalVisible} onOpenChange={(open) => { if (!open) onDeleteInterjectionGroupCancel(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-gc-med text-foreground">
              Confirm Delete Interjection Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm font-gc-reg text-foreground">
              Are you sure you want to delete this interjection group? This action cannot be undone.
            </p>
            {interjectionGroupToDelete && (
              <div className="mt-4 p-3 bg-accent/20 border border-border rounded-md">
                <div className="space-y-1">
                  <p className="text-sm font-gc-med text-foreground">
                    Type: {interjectionGroupToDelete.type === "other" ? interjectionGroupToDelete.customTypeName : interjectionGroupToDelete.type.toUpperCase()}
                  </p>
                  <p className="text-sm font-gc-reg text-foreground">
                    Content Items: {interjectionGroupToDelete.content?.length || 0}
                  </p>
                  <p className="text-sm font-gc-reg text-foreground">
                    Rate: {interjectionGroupToDelete.interjection_rate ? (interjectionGroupToDelete.interjection_rate * 100).toFixed(0) : 0}%
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onDeleteInterjectionGroupCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onDeleteInterjectionGroupConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteModals; 